import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
	NotificationService,
	Notification,
	NotificationPagination
} from '../services/notification.service';
import { ToastService } from '../services/toast.service';
import { AuthStore } from './auth.store';
import { BankAccountsStore } from './bank-accounts.store';

const POLLING_INTERVAL = 30_000;
const PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class NotificationStore {
	private notificationService = inject(NotificationService);
	private authStore = inject(AuthStore);
	private bankAccountsStore = inject(BankAccountsStore);
	private toastService = inject(ToastService);
	private router = inject(Router);

	private pollingIntervalId: any = null;
	private previousUnreadCount = 0;
	private lastAccountNumber: string | undefined;

	private get currentAccountNumber(): string | undefined {
		return this.bankAccountsStore.selectedBankAccount()?.accountNumber;
	}

	// State
	notifications = signal<Notification[]>([]);
	pagination = signal<NotificationPagination | undefined>(undefined);
	unreadCount = signal<number>(0);
	loading = signal(false);
	loadingMore = signal(false);
	error = signal<string | undefined>(undefined);
	currentPage = signal(1);

	// Computed
	hasUnread = computed(() => this.unreadCount() > 0);
	hasNextPage = computed(() => this.pagination()?.hasNextPage ?? false);
	canLoadMore = computed(() => this.hasNextPage() && !this.loading() && !this.loadingMore());

	constructor() {
		// React to account changes
		effect(() => {
			const accountNumber = this.bankAccountsStore.selectedBankAccount()?.accountNumber;
			if (accountNumber !== this.lastAccountNumber) {
				this.lastAccountNumber = accountNumber;
				this.resetNotifications();
				if (this.isAuthenticated()) {
					this.loadUnreadCount();
					this.loadNotifications({ page: 1 });
				}
			}
		});
	}

	// ── Polling ────────────────────────────────────────────────

	private isAuthenticated(): boolean {
		return !!this.authStore.token() && !this.authStore.closingSession();
	}

	startPolling() {
		this.stopPolling();
		this.loadUnreadCount();

		this.pollingIntervalId = setInterval(() => {
			if (document.hidden) return;
			this.pollUnreadCount();
		}, POLLING_INTERVAL);
	}

	stopPolling() {
		if (this.pollingIntervalId) {
			clearInterval(this.pollingIntervalId);
			this.pollingIntervalId = null;
		}
	}

	private async pollUnreadCount() {
		if (!this.isAuthenticated()) return;
		const result = await this.notificationService.getMyUnreadCount(this.currentAccountNumber);
		if (result.data == null) return;

		const newCount = result.data;
		const oldCount = this.previousUnreadCount;
		this.unreadCount.set(newCount);
		this.previousUnreadCount = newCount;

		if (newCount > oldCount && oldCount >= 0) {
			this.showNewNotificationToast(newCount - oldCount);
		}
	}

	private showNewNotificationToast(count: number) {
		const url = this.router.url;
		const isOnNotificationsPage = url.startsWith('/notifications');
		if (isOnNotificationsPage) {
			// If already on the page, silently refresh the list
			this.currentPage.set(1);
			this.loadNotifications({ page: 1, limit: PAGE_SIZE });
			return;
		}

		// Don't interrupt a bidder on the auction detail page (/auctions/:id) with a
		// floating toast — it distracts while participating. The unread-count badge
		// still updates silently from pollUnreadCount.
		const isOnAuctionDetail = /^\/auctions\/[^/]+/.test(url);
		if (isOnAuctionDetail) return;

		const message = count === 1
			? 'Tienes una nueva notificación'
			: `Tienes ${count} nuevas notificaciones`;

		this.toastService.info(message, {
			duration: 6000,
			actions: [{
				label: 'Ver',
				handler: () => this.router.navigate(['/notifications']),
				primary: true,
			}],
		});
	}

	// ── Load / Pagination ──────────────────────────────────────

	async loadNotifications(params?: {
		page?: number;
		limit?: number;
		category?: string;
		type?: string;
		read?: boolean;
	}, append = false) {
		if (append) {
			this.loadingMore.set(true);
		} else {
			this.loading.set(true);
		}
		this.error.set(undefined);

		const result = await this.notificationService.getMyNotifications({
			...params,
			limit: params?.limit ?? PAGE_SIZE,
			sortBy: 'createdAt',
			sortOrder: 'DESC',
			accountNumber: this.currentAccountNumber,
		});

		if (result.data) {
			if (append && this.notifications().length > 0) {
				this.notifications.update((existing) => [...existing, ...result.data!.elements]);
			} else {
				this.notifications.set(result.data.elements);
			}
			this.pagination.set(result.data.pagination);
		} else {
			this.error.set(result.message || 'Error al cargar notificaciones');
		}

		this.loading.set(false);
		this.loadingMore.set(false);
	}

	async loadMore() {
		if (!this.canLoadMore()) return;

		const nextPage = this.currentPage() + 1;
		this.currentPage.set(nextPage);
		await this.loadNotifications({ page: nextPage, limit: PAGE_SIZE }, true);
	}

	resetNotifications() {
		this.notifications.set([]);
		this.pagination.set(undefined);
		this.currentPage.set(1);
		this.error.set(undefined);
	}

	// ── Unread count ───────────────────────────────────────────

	async loadUnreadCount() {
		const result = await this.notificationService.getMyUnreadCount(this.currentAccountNumber);
		if (typeof result.data === 'number') {
			this.unreadCount.set(result.data);
			this.previousUnreadCount = result.data;
		}
	}

	// ── Actions ────────────────────────────────────────────────

	async markAsRead(id: string) {
		const result = await this.notificationService.markAsRead(id);
		if (result.data) {
			this.notifications.update((list) =>
				list.map((n) => (n._id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n))
			);
			this.unreadCount.update((c) => Math.max(0, c - 1));
			this.previousUnreadCount = this.unreadCount();
		}
	}

	async markAllAsRead() {
		const result = await this.notificationService.markAllAsRead(this.currentAccountNumber);
		if (result.data) {
			this.notifications.update((list) =>
				list.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() }))
			);
			this.unreadCount.set(0);
			this.previousUnreadCount = 0;
		}
	}

	async deleteNotification(id: string) {
		const notificationToDelete = this.notifications().find((n) => n._id === id);
		const wasUnread = notificationToDelete !== undefined && !notificationToDelete.read;
		const result = await this.notificationService.deleteNotification(id);
		if (result.data) {
			this.notifications.update((list) => list.filter((n) => n._id !== id));
			if (wasUnread) {
				this.unreadCount.update((c) => Math.max(0, c - 1));
				this.previousUnreadCount = this.unreadCount();
			}
		}
	}
}
